import RuleRow from 'owa-rules-option-common/lib/components/RuleRow';
import setSweepRule from '../actions/setSweepRule';
import SweepRulesListOptionState from '../store/schema/SweepRulesListOptionState';
import store from '../store/store';
import { OptionDescription } from 'owa-options-controls';
import SetSweepRuleOperation from 'owa-service/lib/contract/SetSweepRuleOperation';
import strings from 'owa-strings/owa-options-strings';
import * as React from 'react';
import { reactive } from 'satcheljs/lib/legacy/react';
import EwsSweepRule from 'owa-service/lib/contract/EwsSweepRule';
import folderStore from 'owa-folders';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import KeepNType from 'owa-service/lib/contract/KeepNType';
import { format } from 'owa-strings';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import getSweepRules from '../actions/getSweepRules';
import { Spinner, SpinnerType } from 'office-ui-fabric-react/lib/Spinner';
import SweepRuleRequestStatus from '../store/schema/SweepRuleRequestStatus';

const styles = require('./SweepRulesFull.scss');

export interface SweepRulesFullProps {
    sweepRulesListState?: SweepRulesListOptionState;
}

@reactive({
    sweepRulesListState: () => store,
})
export default class SweepRulesFull extends React.Component<SweepRulesFullProps, {}> {
    render() {
        return (
            <div>
                <OptionDescription>{strings.options_sweepRules_description}</OptionDescription>
                {this.renderRuleListContent()}
            </div>
        );
    }

    /**
     * Creates the rule list content in sweep rules settings page
     * @return content of the sweep rules option page
     */
    private renderRuleListContent() {
        switch (store.getSweepRulesRequestStatus) {
            case SweepRuleRequestStatus.Complete:
                return this.renderRuleRows();
            case SweepRuleRequestStatus.Error:
                return this.getSweepRulesError();
            case SweepRuleRequestStatus.Pending:
            default:
                return <Spinner className={styles.loadingSpinner} type={SpinnerType.large} />;
        }
    }

    /**
     * Creates the rule rows in sweep rules settings page
     * @return sweep rule rows
     */
    private renderRuleRows(): JSX.Element {
        let rows: JSX.Element[] = [];
        const sweepRuleIds = this.props.sweepRulesListState.sweepRuleIds;

        sweepRuleIds.forEach(sweepRuleId => {
            const sweepRule = this.props.sweepRulesListState.sweepRules.get(sweepRuleId);
            const isEnabled = sweepRule.IsEnabled;
            const isInError = this.isSweepRuleError(sweepRule);
            let alertString = isInError ? strings.options_sweepRules_alertString : null;

            rows.push(
                <RuleRow
                    key={sweepRuleId}
                    ruleName={sweepRule.Name}
                    ruleDescription={this.getDescriptionText(sweepRule)}
                    alertString={alertString}
                    isDisabled={isInError}
                    isChecked={isEnabled}
                    enableDisableRule={() => {
                        this.onEnableDisableSweepRule(sweepRuleId, !isEnabled);
                    }}
                    removeRule={() => {
                        this.onRemoveSweepRule(sweepRuleId);
                    }}
                />
            );
        });

        return (
            <div className={styles.sweepRuleRowsContainer}>
                {rows.length > 0 ? (
                    rows
                ) : (
                    <OptionDescription className={styles.emptyRules}>
                        {strings.options_rules_noRules}
                    </OptionDescription>
                )}
            </div>
        );
    }

    private getDescriptionText = (sweepRule: EwsSweepRule): string => {
        const senderSMTP = sweepRule.SweepConditions[0].SenderSmtpAddress;
        const sourceFolder = folderStore.folderTable.get(
            sweepRule.SweepConditions[0].SourceFolderId
        );
        const destinationFolder = folderStore.folderTable.get(
            sweepRule.SweepActions[0].DestinationFolderId
        );
        const keepNType = sweepRule.SweepActions[0].CleanUpType;
        const keepCount = sweepRule.SweepActions[0].KeepCount;

        let conditionString: string;
        let actionString: string;

        // If moving messages from a folder other than inbox, include source folder name in description
        if (!sourceFolder) {
            conditionString = format(
                strings.options_sweepRules_messagesFromIn,
                senderSMTP,
                strings.options_sweepRules_folderNotFound
            );
        } else if (sourceFolder.FolderId.Id != folderNameToId('inbox')) {
            conditionString = format(
                strings.options_sweepRules_messagesFromIn,
                senderSMTP,
                sourceFolder.DisplayName
            );
        } else {
            conditionString = format(strings.options_sweepRules_messagesFrom, senderSMTP);
        }

        // Generate action string. Archive and delete destination folders have specific strings
        if (destinationFolder && destinationFolder.FolderId.Id == folderNameToId('deleteditems')) {
            actionString = this.getDeleteArchiveActionString(
                keepNType,
                keepCount,
                strings.options_sweepRules_delete
            );
        } else if (
            destinationFolder &&
            destinationFolder.FolderId.Id == folderNameToId('archive')
        ) {
            actionString = this.getDeleteArchiveActionString(
                keepNType,
                keepCount,
                strings.options_sweepRules_archive
            );
        } else {
            const destinationDisplayName = destinationFolder
                ? destinationFolder.DisplayName
                : strings.options_sweepRules_folderNotFound;
            if (keepNType == KeepNType.Items) {
                actionString =
                    keepCount == 1
                        ? format(strings.options_sweepRules_keepLatestMove, destinationDisplayName)
                        : format(
                              strings.options_sweepRules_keepNMove,
                              keepCount,
                              destinationDisplayName
                          );
            } else {
                actionString =
                    keepCount == 1
                        ? format(strings.options_sweepRules_keepLastDayMove, destinationDisplayName)
                        : format(
                              strings.options_sweepRules_keepNDaysMove,
                              keepCount,
                              destinationDisplayName
                          );
            }
        }

        return conditionString + actionString;
    };

    private getDeleteArchiveActionString = (
        keepNType: KeepNType,
        keepCount: number,
        deleteArchiveString: string
    ): string => {
        if (keepNType == KeepNType.Items) {
            return keepCount == 1
                ? format(strings.options_sweepRules_keepLatestDeleteArchive, deleteArchiveString)
                : format(
                      strings.options_sweepRules_keepNDeleteArchive,
                      keepCount,
                      deleteArchiveString
                  );
        } else {
            return keepCount == 1
                ? format(strings.options_sweepRules_keepLastDayDeleteArchive, deleteArchiveString)
                : format(
                      strings.options_sweepRules_keepNDaysDeleteArchive,
                      deleteArchiveString,
                      keepCount
                  );
        }
    };

    /**
     * Helper method that handles user click on toggle
     * @param shouldBeEnabled whether rule should be enabled
     * @param sweepRuleId sweep rule id
     */
    private onEnableDisableSweepRule = (sweepRuleId: string, shouldBeEnabled: boolean) => {
        const sweepOperation = shouldBeEnabled
            ? SetSweepRuleOperation.Enable
            : SetSweepRuleOperation.Disable;
        setSweepRule(sweepRuleId, sweepOperation);
    };

    /**
     * Helper method that handles user click on delete
     * @param sweepRuleId sweep rule id
     */
    private onRemoveSweepRule = (sweepRuleId: string) => {
        setSweepRule(sweepRuleId, SetSweepRuleOperation.Delete);
    };

    private isSweepRuleError = (sweepRule: EwsSweepRule) => {
        // An invalid sweep rule is marked by
        // 1) a server property
        // 2) source folder no longer exists
        // 3) destination folder no longer exists
        let sourceFolder = folderStore.folderTable.get(sweepRule.SweepConditions[0].SourceFolderId);
        let destinationFolder = folderStore.folderTable.get(
            sweepRule.SweepActions[0].DestinationFolderId
        );
        return sweepRule.IsInError || !sourceFolder || !destinationFolder;
    };

    /**
     * Creates error message div on get sweep rules error
     * @return error message div
     */
    private getSweepRulesError(): JSX.Element {
        return (
            <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline={false}
                actions={
                    <DefaultButton
                        onClick={() => {
                            getSweepRules();
                        }}>
                        {strings.options_rules_tryAgain}
                    </DefaultButton>
                }>
                {strings.options_rules_loadingRulesError}
            </MessageBar>
        );
    }
}
